/**
 * Multi-agent status constants.
 *
 * Centralises run-status enumerations and display maps so that
 * every component that needs to ask "is this run terminal?" or
 * "what dot + label do I show for status X?" reads from a single
 * source of truth rather than redefining the same sets inline.
 */
import type { DotStatus } from "@/components/atoms/multi-agent/StatusDot";

/** Terminal statuses — a run in one of these states will never advance further. */
export const FINAL_STATUSES = new Set(["done", "failed", "cancelled"]);

/** Live / in-flight statuses — the run is actively producing output. */
export const LIVE_STATUSES = new Set([
  "starting",
  "running",
  "planning",
  "awaiting_approval",
]);

/** Maps each run status to the status-dot variant and human label shown in the header. */
export const STATUS_DOT_MAP: Record<string, { dot: DotStatus; label: string }> = {
  idle:               { dot: "neutral", label: "Idle" },
  starting:           { dot: "warn",    label: "Starting" },
  planning:           { dot: "busy",    label: "Planning" },
  awaiting_approval:  { dot: "warn",    label: "Awaiting approval" },
  running:            { dot: "busy",    label: "Running" },
  done:               { dot: "ok",      label: "Done" },
  failed:             { dot: "err",     label: "Failed" },
  cancelled:          { dot: "neutral", label: "Cancelled" },
  /*
   * Protocol v1 only (`weaveChrome`). A run whose blocking interaction's TTL lapsed is terminal but
   * neither done nor cancelled — the answer is a NEW run carrying `resume_from` (contract §5.3), and
   * the resume card says so. Amber rather than neutral because something is still owed.
   */
  expired:            { dot: "warn",    label: "Expired" },
};

/** Maps agent-node status strings to the StatusDot variant rendered in the right rail. */
export const NODE_DOT: Record<string, DotStatus> = {
  planned:     "neutral",
  ready:       "neutral",
  running:     "busy",
  tool_calling: "busy",
  done:        "ok",
  // Terminal-but-partial (GUI_SYNC_SPEC 2026-08-29 P0-1) — amber, not the
  // grey `?? "neutral"` fallback that made a gapped finish look unstarted.
  completed_with_gaps: "warn",
  failed:      "err",
  skipped:     "neutral",
};
